import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getReturnsFormList,
  generateRegularFile,
  getDownloadFilesList,
  refreshReturnsData,
  downloadReturnsFileExcel,
} from '../api/returnsStep.js'
import { ROLE_CAPABILITIES } from '../utils/role-capabilities.js'
import { DEFAULT_PAGE, getValidCriteria } from '../constants/index.js'
import { searchFields } from '../constants/returnsStep.jsx'
import { fetchEntityListIfNeeded } from '../store/actions/entityTanActions.js'
import {
  getFormVersion,
  getFormType,
  isNewFormVersion,
} from '../utils/formVersionUtils.js'
import { GLOBAL_CONSTANTS } from '../constants/globalConstants.js'

export const useReturnsStep = ({ showSnackbar }) => {
  // Form version state (legacy for 26Q/27Q, new for 140/144)
  const [formVersion, setFormVersion] = useState('legacy')
  const [effectiveFormType, setEffectiveFormType] = useState(null)

  // Dialog states
  const [generateFVUDialogOpen, setgenerateFVUDialogOpen] = useState(false)
  const [downloadFilesDialogOpen, setDownloadFilesDialogOpen] = useState(false)

  // Tab and view states
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [editMode, setEditMode] = useState(false)
  const [isFormView, setIsFormView] = useState(0)
  const [showtable, setShowTable] = useState(true)

  // Data states
  const [tableData, setTableData] = useState([])
  const [dowloadFileList, setDownloadFileList] = useState([])
  const [defaultSearchCOList, setDefaultSearchCOList] = useState([])
  const [tanMasterUuid, setTanMasterUuid] = useState(null)
  const [viewModetanMasterUuid, setViewModeTanMasterUuid] = useState(null)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)

  // Loading states
  const [loading, setLoading] = useState(false)
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [refreshLoadingRowId, setRefreshLoadingRowId] = useState(null)

  // Search states
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])

  const dispatch = useDispatch()
  const roleTypes = useSelector(state => state.user.role)
  const roleConfig = ROLE_CAPABILITIES[roleTypes] || ROLE_CAPABILITIES.default

  // Initialize entity list
  useEffect(() => {
    const payload = {
      pageFilter: {
        currentPage: 1,
        includeCount: true,
        pageSize: DEFAULT_PAGE,
        sortField: 'entityName',
        sortOrder: 'ASC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: [],
    }
    if (roleConfig.canCreate) {
      dispatch(fetchEntityListIfNeeded(payload))
    }
  }, [dispatch, roleConfig.canCreate])

  // Determine form version when financial year changes
  useEffect(() => {
    const financialYear =
      defaultSearchCOList?.year || defaultSearchCOList?.financialYear
    if (financialYear) {
      const version = getFormVersion(financialYear)
      setFormVersion(version)
      const formType =
        defaultSearchCOList?.form || defaultSearchCOList?.formType
      if (formType) {
        setEffectiveFormType(getFormType(financialYear, formType))
      }
    }
  }, [defaultSearchCOList])

  // Fetch returns form list
  const fetchReturnsForm = async ({
    page = 1,
    pageSize = itemsPerPage,
    tanId = tanMasterUuid,
    defaultSearch = defaultSearchCOList,
    searchList = [],
    showAlreadyCreatedAlert = false,
    showUnallocatedChallanAlert = false,
  }) => {
    const { tan, year, entityName, returnType, quarter, form } =
      defaultSearch || {}
    setLoading(true)

    try {
      const payload = {
        pageFilter: {
          includeCount: false,
          currentPage: page,
          sortField: 'createdDate',
          sortOrder: 'DESC',
          pageSize,
        },
        tanUuid: tanId,
        tanUuids: tanId ? tanId.split(',') : [],
        entityTANs: tan ? tan.split(',') : [],
        entityName: entityName,
        returnType: returnType,
        formType: form,
        quarter,
        financialYear: year,
        searchCOList: searchList,
      }
      const resp = await getReturnsFormList(payload)      
      setTableData(resp.data.content)
      setTotalItems(resp.data.totalElements)
      if (showAlreadyCreatedAlert && resp?.data?.content[0]?.alreadyCreated) {
        showSnackbar('Return already generated for the selected filter', 'info')
      }
      if (
        showUnallocatedChallanAlert &&
        resp?.data?.content?.some(item => item?.unallocatedChallan)
      ) {
        showSnackbar(
          GLOBAL_CONSTANTS.RETURNS_UNALLOCATED_CHALLAN_WARNING,
          'warning',
          { vertical: 'top', horizontal: 'right' },
          10000
        )
      }
    } catch (error) {
      showSnackbar(error?.message || 'Failed to fetch returns form', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Fetch download files
  const fetchDownloadFiles = async () => {
    const {
      entityTan,
      entityName,
      returnType,
      formType,
      quarter,
      financialYear,
    } = defaultSearchCOList || {}
    const payload = {
      pageFilter: {
        includeCount: false,
        currentPage: currentPage,
        pageSize: itemsPerPage,
        sortField: 'entityTAN',
        sortOrder: 'DESC',
      },
      tanUuid: tanMasterUuid,
      entityTAN: entityTan,
      entityName,
      returnType: returnType?.toUpperCase(),
      formType: formType,
      quarter,
      financialYear: financialYear,
    }

    try {
      const resp = await getDownloadFilesList(payload)
      if (resp?.data?.success) {
        setDownloadFilesDialogOpen(true)
        setDownloadFileList(resp.data.data)
      } else {
        showSnackbar(resp?.data?.errors.pop()?.message, 'error')
      }
    } catch (error) {
      showSnackbar(error?.message || 'Failed to fetch download files', 'error')
    }
  }

  // Generate regular file
  const handleGenerateRegularFile = async () => {
    const {
      entityTan,
      entityName,
      returnType,
      formType,
      quarter,
      financialYear,
      tanUuid,
    } = defaultSearchCOList || {}
    const payload = {
      pageFilter: {
        includeCount: false,
        currentPage: currentPage,
        pageSize: itemsPerPage,
        sortField: 'entityTAN',
        sortOrder: 'DESC',
      },
      tanUuid: tanUuid,
      entityTAN: entityTan,
      entityName,
      returnType: returnType?.toUpperCase(),
      formType: formType,
      quarter,
      financialYear: financialYear,
    }

    try {
      const regularFileResp = await generateRegularFile(payload)
      if (regularFileResp.data.success) {
        showSnackbar('File Generated Successfully!', 'success')
      } else {
        showSnackbar(regularFileResp?.data?.errors.pop()?.message, 'error')
      }
    } catch (error) {
      showSnackbar(error?.message || 'Failed to generate regular file', 'error')
    }
  }

  // Handlers
  const handleViewClick = async data => {
    setTanMasterUuid(data.tanMasterUuid)
    await fetchReturnsForm({
      page: 1,
      pageSize: itemsPerPage,
      tanId: data.tanMasterUuid,
      defaultSearch: data,
      searchList: [],
      showAlreadyCreatedAlert: true,
      showUnallocatedChallanAlert: true,
    })
  }

  useEffect(() => {
    const loadData = async () => {
      await fetchReturnsForm({
        page: 1,
        pageSize: itemsPerPage,
        tanId: null,
        defaultSearch: {},
        searchList: [],
      })
    }

    loadData()
  }, [])

  const handleRefreshData = async row => {
    if (refreshLoadingRowId) return
    setRefreshLoadingRowId(row.uuid)
    try {
      const payload = {
        pageFilter: {
          includeCount: false,
          currentPage: currentPage,
          sortField: 'createdDate',
          sortOrder: 'DESC',
          pageSize: itemsPerPage,
        },
        tanUuid: row.tanUuid,
        entityTAN: row.entityTan,
        entityName: row.entityName,
        returnType: row.returnType,
        formType: row.formType,
        quarter: row.quarter,
        financialYear: row.financialYear,
        searchCOList: [],
      }
      const resp = await refreshReturnsData(payload)
      if (resp?.data?.success) {
        await fetchReturnsForm({
          page: 1,
          pageSize: itemsPerPage,
          tanId: null,
          defaultSearch: {},
          searchList: [],
        })
        if (
          resp.data.data ===
          GLOBAL_CONSTANTS.RETURNS_UNALLOCATED_CHALLAN_WARNING
        ) {
          showSnackbar(
            resp.data.data,
            'warning',
            { vertical: 'top', horizontal: 'right' },
            10000
          )
        } else {
          showSnackbar(resp.data.data, 'success')
        }
      } else {
        showSnackbar(resp?.data?.errors.pop()?.message, 'error')
      }
    } finally {
      setRefreshLoadingRowId(null)
    }
  }

  const handlePageChange = async page => {
    setCurrentPage(page)
    await fetchReturnsForm({
      page,
      pageSize: itemsPerPage,
      searchList: getValidCriteria(searchCriteria),
      tanId: tanMasterUuid,
    })
  }

  const handleItemsPerPageChange = async newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    await fetchReturnsForm({
      page: 1,
      pageSize: newItemsPerPage,
      searchList: getValidCriteria(searchCriteria),
      tanId: tanMasterUuid,
    })
  }

  const handleApplySearch = async searchList => {
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)

    await fetchReturnsForm({
      page: 1,
      pageSize: itemsPerPage,
      searchList,
      tanId: tanMasterUuid,
    })

    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    setSearchOpen(false)
    await fetchReturnsForm({
      page: 1,
      pageSize: itemsPerPage,
      tanId: tanMasterUuid,
      searchList: [],
    })
  }

  const handleBulkDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    setIsDownloadLoading(true)
    const {
      entityTan,
      entityName,
      returnType,
      formType,
      quarter,
      financialYear,
    } = defaultSearchCOList || {}
    const payload = {
      pageFilter: {
        includeCount: false,
        currentPage: currentPage,
        sortField: 'createdDate',
        sortOrder: 'DESC',
        pageSize: itemsPerPage,
      },
      tanUuid: tanMasterUuid,
      entityTAN: entityTan,
      entityName: entityName,
      returnType: returnType,
      formType: formType,
      quarter,
      financialYear: financialYear,
    }
    // Add your bulk download logic here
    const result = await downloadReturnsFileExcel(payload)
    if (result?.success && result?.data)
    showSnackbar(
      `Request Initiated for Download With Id: ${result.data.data}`,
      'success'
    )
    setIsDownloadLoading(false)
  }

  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }

  return {
    generateFVUDialogOpen,
    setgenerateFVUDialogOpen,
    downloadFilesDialogOpen,
    setDownloadFilesDialogOpen,
    activeTabIndex,
    setActiveTabIndex,
    editMode,
    setEditMode,
    isFormView,
    setIsFormView,
    showtable,
    tableData,
    dowloadFileList,
    defaultSearchCOList,
    tanMasterUuid,
    currentPage,
    itemsPerPage,
    totalItems,
    loading,
    isDownloadLoading,
    isSaving,
    setIsSaving,
    searchOpen,
    anchorEl,
    searchCriteria,
    setSearchCriteria,
    roleConfig,
    viewModetanMasterUuid,
    setDefaultSearchCOList,
    setViewModeTanMasterUuid,

    // Methods
    fetchReturnsForm,
    fetchDownloadFiles,
    handleGenerateRegularFile,
    handleViewClick,
    handleRefreshData,
    refreshLoadingRowId,
    handlePageChange,
    handleItemsPerPageChange,
    handleApplySearch,
    handleResetSearch,
    handleBulkDownload,
    handleSearch,
    handleCloseSearch,

    // Form version (PNW-942)
    formVersion,
    effectiveFormType,
    isNewFormVersion: isNewFormVersion,
  }
}
